import { Button, CornerIconButton } from "@/src/components/buttons";
import SearchBar from "@/src/components/searchBar";
import StaggeredAnimation from "@/src/components/staggeredAnimationComponent";
import { openToast } from "@/src/utils/toasts";
import { MouseEventHandler, useRef, useState } from "react";
import { FaRegCircle } from "react-icons/fa";
import { FaCircleCheck } from "react-icons/fa6";
import { IoMdAdd, IoMdCloseCircleOutline } from "react-icons/io";
import { AiDescription } from "../app/aiTasks/aiDescription";
import { Reference } from "../data/allTypes";
import apiFetch from "../utils/apiFetch";
import { getUserId } from "../utils/utils";
import SearchKeyHighliter from "./seachkeyHighlighter";

export type CardColor =
  | "simple"
  | "gray"
  | "red"
  | "yellow"
  | "green"
  | "blue"
  | "orange"
  | "purple"
  | "pink"
  | "white";

export const cardColors = {
  simple: {
    id: "simple",
    color: "rgba(0, 85, 255,1)",
    secondaryColor: "rgba(255, 255, 255,1)",
  },
  gray: {
    id: "gray",
    color: "rgba(0, 0, 0,0.8)",
    secondaryColor: "rgba(0, 0, 0,0.1)",
  },
  red: {
    id: "red",
    color: "rgba(255, 0, 0,1)",
    secondaryColor: "rgba(255, 0, 0,0.1)",
  },

  green: {
    id: "green",
    color: "rgba(2, 194, 75, 1)",
    secondaryColor: "rgba(2, 194, 75,0.12)",
  },
  blue: {
    id: "blue",
    color: "rgba(0, 115, 255,1)",
    secondaryColor: "rgb(0, 164, 255, 0.3)",
  },
  yellow: {
    id: "yellow",
    color: "rgba(255, 193, 7,1)",
    secondaryColor: "rgba(255, 193, 7,0.1)",
  },
  orange: {
    id: "orange",
    color: "rgba(255, 124, 2,1)",
    secondaryColor: "rgba(255, 124, 2,0.1)",
  },
  purple: {
    id: "purple",
    color: "rgba(144, 0, 255,1)",
    secondaryColor: "rgba(144, 0, 255,0.1)",
  },
  pink: {
    id: "pink",
    color: "rgba(255, 77, 115,1)",
    secondaryColor: "rgba(255, 77, 115,0.1)",
  },
  white: {
    id: "white",
    color: "rgba(255, 255, 255,1)",
    secondaryColor: "rgba(255, 255, 255,0.1)",
  },
};

export function Card({
  color = "simple",
  gap,
  className,
  animationIndex,
  title,
  subTitle,
  leading,
  trailing,
  onClick,
  onEdit,
  onMore,
  onDelete,
  children,
}: {
  color?: CardColor;
  className?: string;
  gap?: string;
  animationIndex?: number;
  title?: any;
  subTitle?: any;
  leading?: any;
  trailing?: any;
  onClick?: (e: any) => void;
  onEdit?: (e: any) => void;
  onMore?: (e: any) => void;
  onDelete?: (e: any) => void;
  children?: any;
}) {
  let typeInfo = cardColors[color];

  const ref = useRef<HTMLDivElement | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  return (
    <StaggeredAnimation index={animationIndex}>
      <AiDescription
        item={{
          type: "card",
          ref,
          title,
          subtitle: subTitle,
          leading,
          trailing,
          content: children,
          isClickable: true,
        }}
      >
        <div
          ref={ref}
          className={`itemCard ${isLoading ? "loading" : ""} ${
            className || ""
          } ${color}-card`}
          style={{
            background: typeInfo?.secondaryColor,
            boxShadow: "none",
            gap: gap
              ? gap
              : leading != null || trailing != null
              ? "15px"
              : undefined,
          }}
          onClick={async (e) => {
            try {
              setIsLoading(true);
              await onClick?.(e);
              setIsLoading(false);
            } catch (e: any) {
              openToast("error", e?.message || e?.toString());
              setIsLoading(false);
              throw e;
            }
          }}
        >
          {onMore && (
            <CornerIconButton onClick={onMore} type="more"></CornerIconButton>
          )}
          {onEdit && (
            <CornerIconButton
              type="edit"
              position={onMore != null ? "secondary" : "primary"}
              onClick={onEdit}
            ></CornerIconButton>
          )}
          {onDelete && (
            <CornerIconButton
              type="delete"
              position={
                onEdit != null || onMore != null ? "secondary" : "primary"
              }
              onClick={async (e) => {
                setIsLoading(true);
                try {
                  await onDelete?.(e);
                } catch (e: any) {
                  setIsLoading(false);
                  throw new Error(e.message);
                }
                setIsLoading(true);
              }}
            ></CornerIconButton>
          )}
          {leading || <div></div>}
          <div className="c1 ">
            {title && (
              <div className="bold title" style={{ color: typeInfo?.color }}>
                {title}
              </div>
            )}
            {subTitle && <div className="gray subTitle">{subTitle || ""}</div>}
            {children}
          </div>
          {trailing}
        </div>
      </AiDescription>
    </StaggeredAnimation>
  );
}

export function CheckboxCard({
  color,
  className,
  status,
  title,
  subTitle,
  trailing,
  onClick,
  onMore,
  onEdit,
  onDelete,
  children,
}: {
  color: CardColor;
  className?: string;
  status: "active" | "pending" | "inactive" | "problem";
  title: any;
  subTitle?: any;
  trailing?: any;
  onClick?: (e: any) => void;
  onMore?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  children?: any;
}) {
  let typeInfo = cardColors[color];
  let colorValue = typeInfo?.color;
  return (
    <Card
      color={color}
      className={className}
      title={title}
      subTitle={subTitle}
      leading={
        <>
          {status == "active" ? (
            <FaCircleCheck size={18} style={{ color: colorValue }} />
          ) : status == "problem" ? (
            <IoMdCloseCircleOutline size={20} style={{ color: colorValue }} />
          ) : (
            <FaRegCircle size={18} style={{ color: colorValue }} />
          )}
        </>
      }
      trailing={trailing}
      onClick={onClick}
      onEdit={onEdit}
      onMore={onMore}
      onDelete={onDelete}
    >
      {children}
    </Card>
  );
}

export function CompartmentCard({
  id,
  color,
  className,
  title,
  buttonTitle,
  onClick,
  secondaryButtonTitle,
  onSecondaryClick,
  searchKey,
  onSearch,
  children,
}: {
  id?: string | undefined;
  color?: CardColor;
  className?: string | null | undefined;
  title: any;
  buttonTitle?: string;
  onClick?: MouseEventHandler;
  secondaryButtonTitle?: any;
  onSecondaryClick?: MouseEventHandler | null | undefined;
  searchKey?: string | null | undefined;
  onSearch?: (newValue: string | null) => void;
  children?: any;
}) {
  let typeInfo = color ? cardColors[color] : null;
  const colorValue = typeInfo?.color;
  const ref = useRef<HTMLDivElement>(null);
  return (
    <AiDescription
      item={{
        type: "compartment",
        title: title,
        description:
          "A compartment is a section of the page that contains a title, a button and some content. Please use the button if you want to add things to the compartment. Do not directly click on the compartment itself. ",
        ref,
        isClickable: false,
      }}
    >
      <div
        id={id}
        className={`compartment relative ${className || ""}`}
        ref={ref}
      >
        <div className="header">
          <div
            className={`compartmentTitle ${colorValue ? "" : "main-color"}`}
            style={{ color: colorValue }}
          >
            {title}
          </div>
          {onSecondaryClick != null ? (
            <Button
              type="tertiary"
              description={`A secondary button made to ${secondaryButtonTitle} `}
              color={color}
              onClick={onSecondaryClick}
            >
              {secondaryButtonTitle || "Add"}
            </Button>
          ) : (
            <div></div>
          )}
          {onClick != null ? (
            <Button onClick={onClick} type={"primary"} color={color}>
              {buttonTitle || <IoMdAdd size={16} />}
            </Button>
          ) : (
            <div></div>
          )}
          {onSearch != null && (
            <div className="triple">
              <SearchBar
                value={searchKey}
                placeholder="Rechercher..."
                onChange={onSearch}
              ></SearchBar>
            </div>
          )}
        </div>

        <div className="content">{children}</div>
      </div>
    </AiDescription>
  );
}

export function StatisticsCard({
  icon,
  title,
  value,
  color,
  onClick,
}: {
  icon?: any;
  title: string;
  value: any;
  color?: "red" | "green" | "blue" | "orange" | "purple" | "brown" | "pink";
  onClick?: () => void;
}) {
  return (
    <div
      className={`statisticsCard  ${color || "blue"} ${
        onClick != null ? "pointer" : ""
      }`}
      onClick={onClick}
    >
      <div className={`icon`}>{icon}</div>
      <div className="c1 gap5">
        <div className="">{title}</div>
        <div className="bold f20">{value}</div>
        {onClick != null && (
          <Button type="tertiary" onClick={onClick}>
            Details
          </Button>
        )}
      </div>
    </div>
  );
}

export function ReferenceCard({
  reference,
  searchKey,
  onClick,
}: {
  reference: Reference;
  searchKey: string;
  onClick?: () => void;
}) {
  const [isDeleted, setIsDeleted] = useState(false);
  if (isDeleted) return <></>;
  return (
    <Card
      color="simple"
      leading={<div></div>}
      onClick={async () => {
        if (!reference.id) return;
        await apiFetch("references", "incrementReferenceCount", {
          referenceId: reference.id,
        });
        onClick?.();
      }}
      trailing={<span className="gray">{reference.usageCount}</span>}
      onDelete={
        (reference.creator?.id || reference.creator) == getUserId()
          ? async () => {
              await apiFetch("general", "deleteAny", {
                id: reference.id,
                table: "references",
              });
              setIsDeleted(true);
            }
          : undefined
      }
    >
      <div className="capitalize">
        <SearchKeyHighliter searchKey={searchKey}>
          {reference.name || ""}
        </SearchKeyHighliter>
      </div>
    </Card>
  );
}
